import { Editor, Path, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';
import React from 'react';
import { v4 as uuid4 } from 'uuid';
import { icons } from '@ublend-npm/aulaui-next';
import { insertVoidElement } from '../../lib/commands';
import { Echo360Entity, echo360Type, isEcho360Element } from './echo360Type';
import { AulaPlugin } from '../plugins';
import { LtiBlock } from '../../components/LtiBlock/LtiBlock';
import { AulaEditorContext, AulaEditorInstance } from '../../types';
import { NestedMenuItem } from '../../components/NestedMenu/NestedMenu';

const ltiEndpoint = 'echo-lti-consumer';

export const insertEcho360 = (
  editor: AulaEditorInstance,
  path: Path,
  context?: Omit<AulaEditorContext, 'entities'>
) => {
  if (context == null) {
    throw new Error('context undefined');
  }

  const { createEntity } = context;
  const linkId = uuid4();
  const entity = createEntity({
    type: 'lti',
    /** An LTI block is accepted to be in the progress of loading data,
     *  if its data  field is  empty. Currently, Echo 360  LTI  blocks
     *  don't receive message after content selection so a random link
     *  id is explicitly set immediately after adding the block to the
     *  document.
     */
    data: { linkId },
    linkId,
  } as Echo360Entity);

  setTimeout(() => {
    // insert at the end of current block
    const end = Editor.end(editor, path);
    Transforms.select(editor, end);
    insertVoidElement(editor, {
      type: echo360Type,
      linkId,
      entity,
    });
  }, 50);
};

export const echo360: AulaPlugin = (editableProps, editor, context) => {
  const { isVoid } = editor;
  const title = 'Echo 360';
  const icon = <icons.Echo360 />;

  editor.isVoid = (element) => {
    if (element.type === echo360Type) return true;

    return isVoid(element);
  };

  return {
    ...editableProps,
    blockMenu: {
      ...editableProps.blockMenu,
      Integrations: {
        ...(editableProps.blockMenu || {}).Integrations,
        [title]: ({ text, element, attributes, onClose }) => (
          <NestedMenuItem
            leftIcon={<icons.Echo360 />}
            key={text}
            text={text}
            attributes={{
              ...attributes,
              onClick: () => {
                onClose?.();
                insertEcho360(
                  editor,
                  ReactEditor.findPath(editor, element),
                  context
                );
              },
            }}
          />
        ),
      },
    },
    renderSubMenuItem: (props) =>
      props.text === 'Integrations' ? (
        <NestedMenuItem
          leftIcon={<icons.ExtensionIcon />}
          rightIcon={<icons.ArrowRight />}
          {...props}
        />
      ) : (
        editableProps?.renderSubMenuItem?.(props) || (
          <NestedMenuItem {...props} />
        )
      ),

    renderElement: (props) => {
      const { element, attributes, children } = props;

      if (isEcho360Element(element)) {
        return (
          <div {...attributes}>
            <LtiBlock
              title={title}
              icon={icon}
              element={element}
              ltiEndpoint={ltiEndpoint}
              scrolling
            />
            {children}
          </div>
        );
      }

      return editableProps.renderElement?.(props) || <p />;
    },
  };
};
